import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { EMAILS, IMG } from "@/lib/brand";
import { Section, PageHeader } from "@/components/common";

const TABS = [
  { key: "projet", label: "Projet artistique", email: EMAILS.contact },
  { key: "management", label: "Contact professionnel", email: EMAILS.management },
  { key: "studio", label: "Studio", email: EMAILS.studio },
];

function Field({ label, children }) {
  return (
    <div>
      <label className="label-dark">{label}</label>
      {children}
    </div>
  );
}

function Consent({ checked, onChange }) {
  return (
    <label className="flex items-start gap-3 text-xs text-slate-400 cursor-pointer">
      <input type="checkbox" data-testid="consent-checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 accent-amber-400 w-4 h-4" />
      <span>J'accepte que mes données soient utilisées pour traiter ma demande, conformément à la politique de confidentialité (RGPD).</span>
    </label>
  );
}

export default function Contact() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab") || "projet";
  const [tab, setTab] = useState(TABS.find((t) => t.key === initial) ? initial : "projet");
  const [loading, setLoading] = useState(false);

  useEffect(() => { const p = params.get("tab"); if (p && TABS.find((t) => t.key === p)) setTab(p); }, [params]);

  const setActive = (k) => { setTab(k); setParams({ tab: k }); };

  const submit = async (endpoint, body, resetForm) => {
    if (!body.consent) { toast.error("Merci d'accepter le consentement RGPD."); return; }
    setLoading(true);
    try {
      await api.post(`/forms/${endpoint}`, body);
      toast.success("Votre demande a bien été envoyée. Merci !");
      resetForm();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow="Contact" title="Parlons de votre projet" subtitle="Trois points d'entrée dédiés selon votre demande. Choisissez le bon interlocuteur." image={IMG.hands} />
      <Section className="py-20 max-w-3xl">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`contact-tab-${t.key}`} onClick={() => setActive(t.key)} className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition ${tab === t.key ? "bg-gold text-black border-transparent" : "border-white/15 text-slate-300 hover:border-amber-400/50"}`}>{t.label}</button>
          ))}
        </div>
        <p className="text-xs text-slate-500 mb-6">Destinataire : <span className="text-gold font-mono">{TABS.find((t) => t.key === tab).email}</span></p>

        {tab === "projet" && <ProjectForm loading={loading} onSubmit={submit} />}
        {tab === "management" && <ManagementForm loading={loading} onSubmit={submit} />}
        {tab === "studio" && <StudioForm loading={loading} onSubmit={submit} />}
      </Section>
    </div>
  );
}

const PROJECT_INIT = { nom: "", prenom: "", nom_artiste: "", email: "", telephone: "", type_projet: "Single", genre: "", description: "", liens: "", reseaux: "", budget: "", echeance: "", message: "", consent: false, website: "" };
function ProjectForm({ loading, onSubmit }) {
  const [f, setF] = useState(PROJECT_INIT);
  const u = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });
  return (
    <form data-testid="project-form" className="space-y-5" onSubmit={(e) => { e.preventDefault(); onSubmit("project", f, () => setF(PROJECT_INIT)); }}>
      <input type="text" className="hidden" value={f.website} onChange={u("website")} tabIndex={-1} autoComplete="off" />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Prénom *"><input required data-testid="pf-prenom" className="input-dark" value={f.prenom} onChange={u("prenom")} /></Field>
        <Field label="Nom *"><input required data-testid="pf-nom" className="input-dark" value={f.nom} onChange={u("nom")} /></Field>
        <Field label="Nom d'artiste"><input className="input-dark" value={f.nom_artiste} onChange={u("nom_artiste")} /></Field>
        <Field label="Genre musical"><input className="input-dark" value={f.genre} onChange={u("genre")} /></Field>
        <Field label="Email *"><input required type="email" data-testid="pf-email" className="input-dark" value={f.email} onChange={u("email")} /></Field>
        <Field label="Téléphone *"><input required data-testid="pf-telephone" className="input-dark" value={f.telephone} onChange={u("telephone")} /></Field>
        <Field label="Type de projet"><select className="input-dark" value={f.type_projet} onChange={u("type_projet")}>{["Single", "EP", "Album", "Clip", "Audiovisuel", "Autre"].map((x) => <option key={x}>{x}</option>)}</select></Field>
        <Field label="Budget"><input className="input-dark" value={f.budget} onChange={u("budget")} /></Field>
        <Field label="Échéance"><input className="input-dark" value={f.echeance} onChange={u("echeance")} /></Field>
        <Field label="Liens d'écoute"><input className="input-dark" value={f.liens} onChange={u("liens")} /></Field>
      </div>
      <Field label="Réseaux sociaux"><input className="input-dark" value={f.reseaux} onChange={u("reseaux")} /></Field>
      <Field label="Description du projet *"><textarea required rows={4} data-testid="pf-description" className="input-dark" value={f.description} onChange={u("description")} /></Field>
      <Field label="Message"><textarea rows={3} className="input-dark" value={f.message} onChange={u("message")} /></Field>
      <Consent checked={f.consent} onChange={(v) => setF({ ...f, consent: v })} />
      <button disabled={loading} data-testid="project-submit" className="btn-gold">{loading ? "Envoi…" : "Envoyer ma demande"}</button>
    </form>
  );
}

const MGMT_INIT = { nom: "", entreprise: "", email: "", telephone: "", type_demande: "Professionnel", message: "", consent: false, website: "" };
function ManagementForm({ loading, onSubmit }) {
  const [f, setF] = useState(MGMT_INIT);
  const u = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });
  return (
    <form data-testid="management-form" className="space-y-5" onSubmit={(e) => { e.preventDefault(); onSubmit("management", f, () => setF(MGMT_INIT)); }}>
      <input type="text" className="hidden" value={f.website} onChange={u("website")} tabIndex={-1} autoComplete="off" />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Nom *"><input required data-testid="mf-nom" className="input-dark" value={f.nom} onChange={u("nom")} /></Field>
        <Field label="Entreprise / structure"><input className="input-dark" value={f.entreprise} onChange={u("entreprise")} /></Field>
        <Field label="Email *"><input required type="email" data-testid="mf-email" className="input-dark" value={f.email} onChange={u("email")} /></Field>
        <Field label="Téléphone *"><input required data-testid="mf-telephone" className="input-dark" value={f.telephone} onChange={u("telephone")} /></Field>
        <Field label="Type de demande"><select className="input-dark" value={f.type_demande} onChange={u("type_demande")}>{["Professionnel", "Média", "Booking", "Partenariat", "Collaboration", "Management", "Autre"].map((x) => <option key={x}>{x}</option>)}</select></Field>
      </div>
      <Field label="Message *"><textarea required rows={5} data-testid="mf-message" className="input-dark" value={f.message} onChange={u("message")} /></Field>
      <Consent checked={f.consent} onChange={(v) => setF({ ...f, consent: v })} />
      <button disabled={loading} data-testid="management-submit" className="btn-gold">{loading ? "Envoi…" : "Envoyer ma demande"}</button>
    </form>
  );
}

const STUDIO_INIT = { nom: "", prenom: "", email: "", telephone: "", service: "Enregistrement", date_souhaitee: "", duree: "", message: "", consent: false, website: "" };
function StudioForm({ loading, onSubmit }) {
  const [f, setF] = useState(STUDIO_INIT);
  const u = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });
  return (
    <form data-testid="studio-form" className="space-y-5" onSubmit={(e) => { e.preventDefault(); onSubmit("studio", f, () => setF(STUDIO_INIT)); }}>
      <input type="text" className="hidden" value={f.website} onChange={u("website")} tabIndex={-1} autoComplete="off" />
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Prénom *"><input required data-testid="sf-prenom" className="input-dark" value={f.prenom} onChange={u("prenom")} /></Field>
        <Field label="Nom *"><input required data-testid="sf-nom" className="input-dark" value={f.nom} onChange={u("nom")} /></Field>
        <Field label="Email *"><input required type="email" data-testid="sf-email" className="input-dark" value={f.email} onChange={u("email")} /></Field>
        <Field label="Téléphone *"><input required data-testid="sf-telephone" className="input-dark" value={f.telephone} onChange={u("telephone")} /></Field>
        <Field label="Service"><select className="input-dark" value={f.service} onChange={u("service")}>{["Enregistrement", "Voix off", "Mixage", "Mastering", "Production sonore", "Autre"].map((x) => <option key={x}>{x}</option>)}</select></Field>
        <Field label="Date souhaitée"><input type="date" className="input-dark" value={f.date_souhaitee} onChange={u("date_souhaitee")} /></Field>
        <Field label="Durée"><input className="input-dark" value={f.duree} onChange={u("duree")} placeholder="ex: 2h" /></Field>
      </div>
      <Field label="Message"><textarea rows={4} className="input-dark" value={f.message} onChange={u("message")} /></Field>
      <Consent checked={f.consent} onChange={(v) => setF({ ...f, consent: v })} />
      <button disabled={loading} data-testid="studio-submit" className="btn-gold">{loading ? "Envoi…" : "Envoyer ma demande"}</button>
    </form>
  );
}
